import { cn } from "@/lib/utils";
import { useTranslation } from "@repo/shared/i18n";
import { Minus, Plus } from "lucide-react";

/*
  The controls beside a stack of identical cards: "x20" copies, and a stepper
  with the completed count between its − and + that marks one copy at a time.
  A column next to the card; the row wraps both in one tinted box, so the
  count reads as the card's own.
*/
export const StackLabels = ({
  completed,
  total,
  onAdd,
  onRemove,
}: {
  completed: number;
  total: number;
  /** Without handlers the stepper has no buttons: a loading row only counts. */
  onAdd?: () => void;
  onRemove?: () => void;
}) => {
  const { t } = useTranslation("sbcs");
  const label =
    "flex h-8 items-center justify-center rounded-md border border-neutral-600 bg-neutral-900 px-2 text-sm font-extrabold leading-none text-white";
  const button =
    "flex size-7 items-center justify-center rounded-sm text-neutral-200 transition-colors hover:bg-neutral-700 disabled:opacity-30 disabled:hover:bg-transparent";
  return (
    <div className="flex flex-col items-stretch justify-center gap-1.5 self-center">
      <span className={cn(label, "text-neutral-300")}>
        {t("streamline.stackCount", { total })}
      </span>
      <span
        className={cn(
          label,
          "gap-0.5 px-0.5",
          completed === total &&
            "border-streamline-success text-streamline-success",
        )}
      >
        {onRemove && (
          <button
            type="button"
            className={button}
            disabled={completed === 0}
            aria-label={t("streamline.removeCopy")}
            onClick={onRemove}
          >
            <Minus className="size-4" />
          </button>
        )}
        <span className="min-w-6 text-center">{completed}</span>
        {onAdd && (
          <button
            type="button"
            className={button}
            disabled={completed === total}
            aria-label={t("streamline.addCopy")}
            onClick={onAdd}
          >
            <Plus className="size-4" />
          </button>
        )}
      </span>
    </div>
  );
};
